import React from 'react';
import { motion } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import { Button, Text, Select } from '@chatsdet/ui';
import type { SuiteFailureMode } from '../../../features/test-suites';

const FAILURE_MODE_OPTIONS = [
  { id: 'continue', label: 'Continue through all layers' },
  { id: 'stop_on_layer_failure', label: 'Stop if a layer fails' },
];

interface SuiteSettingsOverlayProps {
  name: string;
  description: string;
  failureMode: SuiteFailureMode;
  onNameChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onFailureModeChange: (v: SuiteFailureMode) => void;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  isSaving: boolean;
}

/**
 * Suite settings as a full-canvas fly-up overlay, mirroring the Flow Designer's
 * WorkflowSettingsOverlay: a framer-motion panel that slides up from the bottom
 * over the canvas. Edits flow into the builder's local dirty state; Save
 * persists and closes. Wrap the render in <AnimatePresence>.
 */
export function SuiteSettingsOverlay({
  name,
  description,
  failureMode,
  onNameChange,
  onDescriptionChange,
  onFailureModeChange,
  onClose,
  onSave,
  isSaving,
}: SuiteSettingsOverlayProps) {
  return (
    <>
      {/* Background — slides up */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0, transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] } }}
        exit={{ y: '100%', transition: { duration: 0.25, delay: 0.2, ease: [0.32, 0.72, 0, 1] } }}
        className="absolute inset-0 z-40 bg-background"
      />

      {/* Close button — top left where the gear was */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1, transition: { duration: 0.2, delay: 0.25 } }}
        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
        className="absolute top-4 left-4 z-50"
      >
        <Button variant="icon" size="md" iconOnly onPress={onClose} tooltip="Close settings">
          <X size={20} weight="bold" />
        </Button>
      </motion.div>

      {/* Form — fades in after the background */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.25, delay: 0.2 } }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        className="absolute inset-0 z-40 flex justify-center overflow-y-auto p-8"
      >
        <div className="my-auto w-full max-w-2xl space-y-6 py-4">
          <div>
            <Text weight="semibold" size="lg" className="mb-1">
              Suite Settings
            </Text>
            <Text size="sm" className="text-muted-foreground">
              Name your suite and choose how it behaves when a layer fails.
            </Text>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <label htmlFor="suite-name" className="text-sm font-medium leading-none">
              Name
            </label>
            <input
              id="suite-name"
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              className="flex h-10 w-full rounded-md border border-border bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder="e.g. Checkout regression"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label htmlFor="suite-description" className="text-sm font-medium leading-none">
              Description
            </label>
            <textarea
              id="suite-description"
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-border bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y min-h-[72px]"
              placeholder="What this suite covers."
            />
          </div>

          {/* On layer failure */}
          <Select
            label="On layer failure"
            items={FAILURE_MODE_OPTIONS}
            selectedKey={failureMode}
            onSelectionChange={(key) => {
              if (!key) return;
              onFailureModeChange(String(key) as SuiteFailureMode);
            }}
          />
          <Text size="xs" className="text-muted-foreground -mt-3">
            Layers run in sequence. &ldquo;Stop if a layer fails&rdquo; skips all
            remaining layers when any test in a layer fails.
          </Text>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onPress={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={isSaving || name.trim().length === 0}
              onPress={() => void onSave()}
            >
              Save
            </Button>
          </div>
        </div>
      </motion.div>
    </>
  );
}
